/**
 * App users (ADR-0010 P7) — manage portal end-users (`app_users`), a realm
 * SEPARATE from operators. Global per instance: provision here, then assign
 * to an app from its Members tab. Supports create, search, enable/disable,
 * and admin password reset. RBAC: tag-scoped `apps` resource.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, ShieldOff, UserCog } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { appUsersApi, type AppUserOut } from "@/lib/api-app-users";
import { usePermissions } from "@/lib/permissions";

export function AppUsersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("apps", "write");
  const [search, setSearch] = React.useState("");
  const [createOpen, setCreateOpen] = React.useState(false);
  const [resetFor, setResetFor] = React.useState<AppUserOut | null>(null);

  const usersQ = useQuery({
    queryKey: ["app-users", "list", search],
    queryFn: () => appUsersApi.list({ search: search || undefined, page_size: 100 }),
  });

  const toggleState = useMutation({
    mutationFn: (u: AppUserOut) =>
      appUsersApi.update(u.id, { state: u.state === "active" ? "disabled" : "active" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["app-users"] }),
  });
  const reset2fa = useMutation({
    mutationFn: (id: string) => appUsersApi.resetTwoFactor(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["app-users"] }),
  });

  const users = usersQ.data?.items ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t("appUsers.title", "App users")}
        description={t(
          "appUsers.subtitle",
          "Portal end-users — a separate realm from operators. Add them to an app from its Members tab.",
        )}
        actions={
          canWrite && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("appUsers.new", "New app user")}
            </Button>
          )
        }
      />
      <PageContent className="space-y-4">
        <Input
          value={search}
          placeholder={t("appUsers.searchHint", "Search by email or name…")}
          className="max-w-sm"
          onChange={(e) => setSearch(e.target.value)}
        />
        {users.length === 0 && !usersQ.isLoading ? (
          <EmptyState
            icon={UserCog}
            title={t("appUsers.empty", "No app users yet")}
            description={t(
              "appUsers.emptyDescription",
              "Create a portal end-user, or let them self-sign-up from an app's portal.",
            )}
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>{t("appUsers.email", "Email")}</Th>
                  <Th>{t("appUsers.fullName", "Name")}</Th>
                  <Th className="w-24">{t("appUsers.state", "State")}</Th>
                  <Th className="w-44">{t("appUsers.lastLogin", "Last login")}</Th>
                  {canWrite && <Th className="w-44 text-right">{t("common.actions", "Actions")}</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {users.map((u) => (
                  <Tr key={u.id}>
                    <Td className="font-medium">{u.email}</Td>
                    <Td className="text-sm text-muted-foreground">{u.full_name ?? "—"}</Td>
                    <Td>
                      <Badge tone={u.state === "active" ? "active" : "danger"}>{u.state}</Badge>
                    </Td>
                    <Td className="text-xs text-muted-foreground">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                    </Td>
                    {canWrite && (
                      <Td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setResetFor(u)}
                            aria-label={t("appUsers.resetPassword", "Reset password")}
                          >
                            <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </Button>
                          {u.totp_enabled && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (window.confirm(t("twoFactor.resetConfirm")))
                                  reset2fa.mutate(u.id);
                              }}
                              aria-label={t("twoFactor.reset")}
                            >
                              <ShieldOff className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleState.mutate(u)}
                          >
                            {u.state === "active"
                              ? t("appUsers.disable", "Disable")
                              : t("appUsers.enable", "Enable")}
                          </Button>
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </PageContent>
      <CreateAppUserDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ResetPasswordDialog user={resetFor} onClose={() => setResetFor(null)} />
    </PageContainer>
  );
}

function CreateAppUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [email, setEmail] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [password, setPassword] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setEmail("");
      setFullName("");
      setPassword("");
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      appUsersApi.create({
        email: email.trim(),
        full_name: fullName.trim() || null,
        password: password.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["app-users"] });
      onOpenChange(false);
    },
  });

  const canSubmit = email.trim().length > 3 && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("appUsers.new", "New app user")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="au-email">{t("appUsers.email", "Email")}</Label>
            <Input id="au-email" value={email} autoFocus onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="au-name">{t("appUsers.fullName", "Name")}</Label>
            <Input id="au-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="au-pass">{t("appUsers.passwordOptional", "Password (optional)")}</Label>
            <Input
              id="au-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t("appUsers.passwordHint", "Leave blank to set it later via reset.")}
            </p>
          </div>
          {create.isError && (
            <Alert tone="danger" title={t("appUsers.createFailed", "Could not create app user")}>
              <span className="font-mono text-xs">{(create.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!canSubmit} onClick={() => create.mutate()}>
            {t("apps.create", "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({
  user,
  onClose,
}: {
  user: AppUserOut | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = React.useState("");

  React.useEffect(() => {
    setPassword("");
  }, [user]);

  const reset = useMutation({
    mutationFn: () => appUsersApi.resetPassword(user!.id, password.trim()),
    onSuccess: onClose,
  });

  const canSubmit = password.trim().length >= 8 && !reset.isPending;

  return (
    <Dialog open={user !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("appUsers.resetPassword", "Reset password")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-muted-foreground">{user?.email}</p>
          <div className="space-y-1.5">
            <Label htmlFor="au-newpass">{t("appUsers.newPassword", "New password")}</Label>
            <Input
              id="au-newpass"
              type="password"
              value={password}
              autoFocus
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {reset.isError && (
            <Alert tone="danger" title={t("appUsers.resetFailed", "Could not reset password")}>
              <span className="font-mono text-xs">{(reset.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!canSubmit} onClick={() => reset.mutate()}>
            {t("appUsers.resetPassword", "Reset password")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
